/* eslint-disable camelcase */
/* eslint-disable no-console */
import type { Channel } from '../stores/types/home';
import { ChannelAuthMethod } from '../stores/types/types';
import type {
	PresenterAllowed,
	CreatePlatformAccountResponse,
} from '../stores/types/connection';
import { getAuthenticatedRequestInit } from './utils';
import { doesEnvVarExist } from '../utils/utils';
import blpAuth from './blp-auth';
import { getApiHost } from '../utils/hosts';
import logger from '../utils/logger-rum';

interface GreenroomTokenResponse {
	token: string;
	app_signed_in_with: string;
	channelIds: number[];
	isProducer?: boolean;
}

export const ProducerLogin = async (
	email: string,
	password: string,
	app: string,
	storedTwoFactorEvents?: string,
) => {
	try {
		const res = await fetch(`${getApiHost()}/v2/greenroom/producer/login/`, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				email,
				password,
				app,
				storedTwoFactorEvents,
			}),
		});
		if (res.ok) return await res.json();
		const errorResponse = await res.json();
		return errorResponse;
	} catch (e) {
		logger.error(e);
		return { message: 'Connection Error' };
	}
};

export const PresenterLogin = async (email: string, password: string) => {
	try {
		const res = await fetch(`${getApiHost()}/v2/greenroom/presenter/login/`, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				email,
				password,
			}),
		});
		if (res.ok) return res.json();
		const errorResponse = await res.json();
		return errorResponse;
	} catch (e) {
		logger.error(e);
		return { message: 'Connection Error' };
	}
};

export const CreateAccount = async (
	firstName: string,
	lastName: string,
	email: string,
	password: string,
	is_producer: boolean,
	verification_code: string | undefined,
) => {
	try {
		const res = await fetch(`${getApiHost()}/v2/greenroom/account`, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				first_name: firstName,
				last_name: lastName,
				email,
				password,
				is_producer,
				verification_code,
			}),
		});

		if (res.ok) {
			return res.json();
		} if (res.status === 400) {
			return { message: 'exists' };
		}
		return { message: 'Response Error' };
	} catch (e) {
		logger.error(e);
		return { message: 'Connection Error' };
	}
};

export const VerifyAccount = async (code: string) => {
	try {
		const res = await fetch(`${getApiHost()}/v2/verify`, {
			method: 'PUT',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				code,
			}),
		});
		if (res.ok) return res.json();
		return { message: 'Response Error' };
	} catch (e) {
		logger.error(e);
		return { message: 'Connection Error' };
	}
};

interface User {
	first_name?: string;
	last_name?: string;
	avatar?: string;
	color?: string;
	title?: string;
	location?: string;
	bg_image?: string
}

export const UpdateProfile = async (
	gr_user: number,
	columns: User,
	token: string,
) => {
	try {
		const res = await fetch(`${getApiHost()}/v3/greenroom/profile`, {
			method: 'PUT',
			body: JSON.stringify({
				gr_user,
				columns,
			}),
			...getAuthenticatedRequestInit(token),
		});

		if (res.ok) {
			const response = await res.json();
			return response.token;
		}
		return { message: 'Response Error' };
	} catch (e) {
		logger.error(e);
		return { message: 'Connection Error' };
	}
};

export const UpdateBgImage = async (token: string, image: string) => {
	try {
		const res = await fetch(`${getApiHost()}/v2/profile/bg-image`, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ token, image }),
		});
		if (res.ok) {
			const response = await res.json();
			return response.token;
		}
		return { message: 'Response Error' };
	} catch (e) {
		logger.error(e);
		return { message: 'Connection Error' };
	}
};

export const ForgotPasswordEvents = async (email: string) => {
	try {
		const res = await fetch(`${getApiHost()}/v2/forgot-password`, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ email, is_greenroom: true }),
		});
		if (res.ok) return true;
	} catch (e) {
		logger.error(e);
	}
	throw new Error('Forgot Password Failure');
};

export const ForgotPasswordGreenroom = async (email: string, isProducer: boolean) => {
	try {
		const res = await fetch(`${getApiHost()}/v2/greenroom/forgot-password`, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ email, is_producer: isProducer }),
		});
		if (res.ok) return true;
		if (res.status === 404) return 404;
	} catch (e) {
		logger.error(e);
	}
	throw new Error('Forgot Password Failure');
};

export const UpdatePasswordEvents = async (password: string, code: string) => {
	try {
		const res = await fetch(`${getApiHost()}/v2/update-password`, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ password, code }),
		});
		if (res.ok) return true;
	} catch (e) {
		logger.error(e);
	}
	throw new Error('Password Reset Failure');
};

export const UpdatePasswordGreenroom = async (password: string, code: string) => {
	try {
		const res = await fetch(`${getApiHost()}/v2/greenroom/update-password`, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ password, code }),
		});
		if (res.ok) return true;
	} catch (e) {
		logger.error(e);
	}
	throw new Error('Password Reset Failure');
};

export const ResendVerificationEmail = async (email: string) => {
	try {
		const res = await fetch(`${getApiHost()}/v2/greenroom/resend-verification`, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ email }),
		});
		if (res.ok) return true;
	} catch (e) {
		logger.error(e);
	}
	throw new Error('Resend Verification Email Failure');
};

export const RefreshToken = async (token: string) => {
	try {
		const res = await fetch(`${getApiHost()}/v2/refresh-token`, {
			method: 'GET',
			...getAuthenticatedRequestInit(token),
		});
		if (res.ok) return res.json();
	} catch (e) {
		logger.error(e);
	}
	throw new Error('Unable to refresh token');
};

export const GetCurrentProfile = async (
	token: string,
	appSignedInWith: string,
	showroomsToken?: string,
) => {
	try {
		const res = await fetch(`${getApiHost()}/v2/greenroom/profile/${appSignedInWith}`, {
			method: 'GET',
			...getAuthenticatedRequestInit(token),
		});
		if (res.ok) return res.json();
	} catch (e) {
		logger.error(e);
	}
	throw new Error('Unable to get current profile');
};

export const TempPhone = async (phone: string, temp_profile: string): Promise<any> => {
	try {
		const res = await fetch(`${getApiHost()}/v2/greenroom/events/set-temp-phone`, {
			method: 'POST',
			body: JSON.stringify({
				temp_profile,
				phone,
			}),
			...getAuthenticatedRequestInit(''),
		});
		if (res.ok) return res.json();
	} catch (e) {
		logger.error(e);
	}
	throw new Error(`${getApiHost()}/v2/greenroom/events/set-temp-phone didn't work`);
};

export const ConfirmCode = async (
	temp_profile: string,
	code: string,
	request_storage: boolean,
): Promise<any> => {
	try {
		const res = await fetch(`${getApiHost()}/v2/greenroom/events/confirm-code`, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				temp_profile,
				code,
				request_storage,
			}),
		});
		if (res.status === 403) return { message: 'incorrect confirmation code given' };
		if (res.ok) return res.json();
	} catch (e) {
		logger.error(e);
	}
	throw new Error(`${getApiHost()}/v2/greenroom/events/confirm-code error confirming code`);
};

export const UpdateChannel = async (token: string, channel: Channel) => {
	try {
		const res = await fetch(`${getApiHost()}/v2/greenroom/admin/channels/update`, {
			method: 'POST',
			body: JSON.stringify(channel),
			...getAuthenticatedRequestInit(token),
		});
		if (res.ok) return channel;
	} catch (e) {
		logger.error(e);
	}
	throw new Error('Unable to update channel');
};

export const isBrandliveEmployee = async (token: string) => {
	try {
		const res = await fetch(`${getApiHost()}/v2/greenroom/is-brandlive-employee`, {
			method: 'GET',
			...getAuthenticatedRequestInit(token),
		});
		if (res.ok) return res.json();
	} catch (e) {
		logger.error(e);
	}
	throw new Error('Unable to check if brandlive user');
};

export const deleteAccountAPI = (token: string) => fetch(`${getApiHost()}/v2/greenroom/user`, {
	method: 'DELETE',
	...getAuthenticatedRequestInit(token),
}).then((result) => {
	if (result.ok) {
		return true;
	}
	return false;
});

export const AddLoginActivity = async (
	token: string,
	activityType: string,
	hostUrl: string,
	appSignedInWith: string | null,
) => {
	try {
		const res = await fetch(`${getApiHost()}/v2/greenroom/login-activity`, {
			method: 'POST',
			body: JSON.stringify({
				activity_type: activityType,
				host_url: hostUrl,
				app_signed_in_with: appSignedInWith,
			}),
			...getAuthenticatedRequestInit(token),
		});
		if (res.ok) {
			return true;
		}
		return false;
	} catch (e) {
		logger.error(e);
	}
	throw new Error('Unable to add login activity');
};

export const GetGreenroomToken = async (
	platformToken: string,
	meetingId?: number,
): Promise<GreenroomTokenResponse | null> => {
	try {
		if (!getApiHost()) {
			logger.error('Please check environment.');

			return null;
		}

		const res = await fetch(`${getApiHost()}/v2/greenroom/auth/token`, {
			method: 'PUT',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
				'X-IS-PRODUCER': 'false',
				'X-MEETING': meetingId ? meetingId.toString() : '',
			},
			body: JSON.stringify({
				platformToken,
			}),
		});

		if (res.ok) {
			const data: GreenroomTokenResponse = await res.json();

			return data;
		}

		throw new Error('Unable to get greenroom token');
	} catch (e) {
		logger.error('GetGreenroomToken error', e);

		return null;
	}
};

export const GetGrUserFromAccessToken = (): Promise<GreenroomTokenResponse | null> => fetch(`${getApiHost()}/v2/greenroom/auth/profile`, {
	headers: {
		'Content-Type': 'application/json',
		Authorization: blpAuth.accessToken ? `Bearer ${blpAuth.accessToken}` : undefined,
	},
	credentials: blpAuth.accessToken ? undefined : 'include', // needed for cookie auth
} as RequestInit).then((res) => {
	if (res.ok && res.headers.get('content-type')?.includes('application/json')) {
		return res.json();
	}

	return null;
}).catch((e: Error) => {
	logger.error(e);
	return null;
});

export const CreatePlatformAccount = async (
	email: string,
	firstName?: string,
	lastName?: string,
	meetingHash?: string,
	redirect?: string,
): Promise<CreatePlatformAccountResponse> => {
	let status = null;

	try {
		if (!getApiHost()) {
			throw new Error('Please check environment.');
		}

		const res = await fetch(`${getApiHost()}/v3/greenroom/account`, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				email,
				...(firstName && { firstName }),
				...(lastName && { lastName }),
				...(meetingHash && { meetingHash }),
				...(redirect && { redirect }),
			}),
		});

		status = res?.status || null;

		if (res?.ok) {
			// res.json() will return => Promise<PresenterCreationReturn>
			// Just worrying about the status since we get the token from Platform
			return {
				status,
				success: true,
			};
		}

		throw new Error('Unable to create platform account.');
	} catch (e) {
		logger.error('CreatePlatformAccount error:', e);

		return {
			status,
			success: false,
		};
	}
};

interface UpdatePresenterAuthResponse {
    platformProfileId: number;
    channelId: number;
    authType: ChannelAuthMethod;
    email: string;
}

export const UpdatePresenterAuth = async (
	token: string,
	platformProfileId: number,
	channelId: number,
	authType: ChannelAuthMethod,
): Promise<UpdatePresenterAuthResponse | null> => {
	let status = null;

	try {
		if (!getApiHost()) {
			throw new Error('Please check environment.');
		}

		const res = await fetch(`${getApiHost()}/v3/greenroom/account/auth`, {
			method: 'PUT',
			body: JSON.stringify({
				platformProfileId,
				channelId,
				authType,
			}),
			...getAuthenticatedRequestInit(token),
		});

		if (res.ok) {
			return res.json();
		}

		status = res.status || null;

		throw new Error('Unable to update presenter auth type.');
	} catch (e) {
		logger.error(`UpdatePresenterAuth: status ${status}, error:`, e);

		return null;
	}
};

export const GetPresenterAllowed = async (
	token: string,
	meetingHash: string,
): Promise<PresenterAllowed | null> => {
	try {
		const envVarExists = doesEnvVarExist('REACT_APP_API_HOST');

		if (!envVarExists) {
			return null;
		}

		const res = await fetch(
			`${getApiHost()}/v2/greenroom/meetings/${meetingHash}/authorized`,
			{
				method: 'GET',
				...getAuthenticatedRequestInit(token),
			},
		);

		if (res.ok) {
			return res.json();
		}

		logger.error('GetPresenterAllowed: Unable to get presenter auth status.');

		return null;
	} catch (e) {
		logger.error('GetPresenterAllowed: error', e);

		return null;
	}
};

export const GetInviteSsoDomains = async (token: string, meetingId: number): Promise<string[]> => {
	try {
		const envVarExists = doesEnvVarExist('REACT_APP_API_HOST');

		if (!envVarExists) {
			return [];
		}

		const res = await fetch(
			`${getApiHost()}/v2/greenroom/auth/domains/${meetingId}`,
			{
				method: 'GET',
				...getAuthenticatedRequestInit(token),
			},
		);

		if (res.ok) {
			return res.json();
		}

		logger.error('getSSODomains: Unable to get SSO domains.');

		return [];
	} catch (e) {
		logger.error('getSSODomains: error', e);

		return [];
	}
};

// fulfilling blpAuth.get.options.headers Record<string,string> with an optional prop
interface NormalIdHeader extends Record<string, string> {'id-token': string}
interface EmbedIdHeader extends NormalIdHeader {'X-IS-EMBED': string}
type EmbedSafeHeader = NormalIdHeader | EmbedIdHeader;
export const getEmbedSafeHeaders = (): EmbedSafeHeader => {
	const nHeaders: EmbedSafeHeader = {'id-token': ''};
	
	const embedToken = new URLSearchParams(location.search).get('embed-token');
	const embedPath = location.pathname.includes('/embed/');
	const isEmbed = (embedPath && embedToken && typeof embedToken === 'string');

	if (isEmbed) {
		nHeaders['X-IS-EMBED'] = 'true';
		nHeaders['id-token'] = embedToken;
	} else if (blpAuth.idToken) {
		nHeaders['id-token'] = blpAuth.idToken;
	}

	return nHeaders;
}